import { Component, Injector } from '@angular/core';
import type { OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

import { ReportsBasePage } from '../reportsBase/reportsBase.page';
import { ReportFactoryService } from '@shared/services';
import type { TReportOptions } from '@shared/models';

@Component({
    'selector': 'app-report3View',
    'templateUrl': './report3View.page.html',
    'styleUrls': ['./report3View.page.scss'],
    'standalone': true,
    'imports': [ CommonModule, FormsModule, IonicModule ]
})
export class Report3ViewPage extends ReportsBasePage implements OnInit {
    // public vm: any = {};
    public isDebugMode = false;
    private originalColWidths: number[] = [];

    constructor(private reportFactoryService: ReportFactoryService, 
        injector: Injector) {
        super(injector);
    }

    public ionViewWillEnter(): void {
        super.setCurrentView(this.Constants.VIEW_REPORTS_PAGE3);
    }

    public override async ngOnInit(): Promise<void> {
        this.vm = super.getViewModel();
        this.vm.reportData = {};
        this.vm.reportData.dataRows = [];
        this.vm.reportData.summaryRows = [];
        this.vm.pages = [];
        this.vm.pageHeader = {};

        await this.generateReport();
        this.initMoreMenu();
    }

    public initMoreMenu(): void {
        this.resetPopoverMenu();
        this.pushPopoverMenuItem('Print');
        // this.pushPopoverMenuItem('Show Tour', 'show-reports-3-tour');

        this.moreMenuItemClick((menuItem: any) => {
            if (menuItem.name === 'Print') {
                this.onPrintClick();
            }
        });
    }

    private async generateReport(): Promise<void> {
        const options: TReportOptions = {
            'selectedReport': this.vm.selectedReport,
            'startingDate': this.vm.mmStartingDate,
            'endingDate': this.vm.mmEndingDate
        };

        try {
            await this.uxHelperService.showSpinner('Loading data.');
            const reportService = this.reportFactoryService.createReportService(options);
            this.vm.reportData = await reportService.getReportData();

            const reportFormatter = this.reportFactoryService.createReportFormatter();
            const reportItems = await reportFormatter.buildReportPages(options, this.vm.reportData);
            this.vm.pageHeader = reportItems.pageHeader;
            this.vm.pages = reportItems.pages;
            
            // Build CSS Grid template columns string
            this.buildGridTemplateColumns();
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    public isSubtotalRow(row) {
        return (row && row.myClass === 'subtotal');
    }

    private buildGridTemplateColumns(): void {
        // Build CSS Grid template columns from the report's column widths
        if (this.vm.selectedReport && this.vm.selectedReport.colWidths) {
            // Store original widths for reset (only first time)
            if (this.originalColWidths.length === 0) {
                this.originalColWidths = [...this.vm.selectedReport.colWidths];
            }
            
            // Make all columns except the last one fixed width
            // Last column gets minmax to be flexible and prevent overflow
            const gridColumns = this.vm.selectedReport.colWidths
                .map((width: number, index: number, arr: number[]) => {
                    if (index === arr.length - 1) {
                        // Last column: minimum width but can grow to fill space
                        return `minmax(${width}px, 1fr)`;
                    }
                    return `${width}px`;
                })
                .join(' ');
            this.vm.gridTemplateColumns = gridColumns;
        } else {
            // Fallback: distribute columns equally
            const numColumns = this.vm.pageHeader?.headerColumns?.length || 1;
            this.vm.gridTemplateColumns = `repeat(${numColumns}, 1fr)`;
        }
    }

    // Debug mode methods for adjusting column widths
    public onHeaderClick(columnIndex: number): void {
        if (this.vm.isDebugMode) {
            this.vm.selectedColumnIndex = this.vm.selectedColumnIndex === columnIndex ? -1 : columnIndex;
        }
    }

    public getColumnWidth(columnIndex: number): number {
        return this.vm.selectedReport?.colWidths?.[columnIndex] || 0;
    }

    public adjustColumnWidth(columnIndex: number, adjustment: number): void {
        if (this.vm.selectedReport && this.vm.selectedReport.colWidths) {
            const currentWidth = this.vm.selectedReport.colWidths[columnIndex];
            const newWidth = Math.max(20, currentWidth + adjustment); // Minimum 20px
            this.vm.selectedReport.colWidths[columnIndex] = newWidth;
            this.buildGridTemplateColumns();
        }
    }

    public async copyWidthsToClipboard(): Promise<void> {
        if (this.vm.selectedReport && this.vm.selectedReport.colWidths) {
            const widthsArray = JSON.stringify(this.vm.selectedReport.colWidths);
            const text = `'colWidths': ${widthsArray},`;
            
            try {
                await navigator.clipboard.writeText(text);
                this.uxHelperService.showToast('Column widths copied to clipboard!');
            } catch (err) {
                // Fallback for older browsers
                // console.log('Copy this to your report configuration:');
                // console.log(text);
                this.uxHelperService.showToast('Check console for column widths');
            }
        }
    }

    public resetColumnWidths(): void {
        if (this.vm.selectedReport && this.originalColWidths.length > 0) {
            this.vm.selectedReport.colWidths = [...this.originalColWidths];
            this.buildGridTemplateColumns();
            this.vm.selectedColumnIndex = -1;
            this.uxHelperService.showToast('Column widths reset to original');
        }
    }

    public onPrintClick() {
        // The last time I checked reportPrint.css was not uploaded.  It was uploaded to Shlepz.com.
        const styleSheet = 'http://www.ShuttleNomix.com/wp-content/Private/reportPrint.css';
        const openingHtml = '<html><head><title></title>';
        const styleSheetLink = '<link rel="stylesheet" type="text/css" href="' + styleSheet + '" />';
        const closingHead = '</head>';    // media="print"

        const reportToPrint = document.getElementById('divToPrint')?.innerHTML;
        const bodyToPrint = '<body><div> ' + reportToPrint + ' </div></body>';
        const closingHtml = '</html>';

        const dimensions = 'height=' + window.screen.height * 0.85 + ', width=' + window.screen.width * 0.95;
        const parms = 'left=20, top=20, toolbar=0, scrollbars=0, status=0';
        const myWindow = window.open('', '', parms + ', ' + dimensions);

        if (myWindow !== null) {
            myWindow.document.write(openingHtml);
            myWindow.document.write(styleSheetLink);

            // To keep styling
            const file = myWindow.document.createElement('link');
            file.setAttribute('rel', 'stylesheet');
            file.setAttribute('type', 'text/css');
            // file.setAttribute('href', 'http://www.ShuttleNomix.com/wp-content/Private/reportPrint.css');
            file.setAttribute('href', styleSheet);

            myWindow.document.head.appendChild(file);

            myWindow.document.write(closingHead);
            myWindow.document.write(bodyToPrint);
            myWindow.document.write(closingHtml);
            myWindow.document.close();
            myWindow.setTimeout(() => {
                myWindow.focus();
                myWindow.print();
                myWindow.close();
            }, 1000);
            myWindow.document.head.appendChild(file);

            myWindow.document.write(closingHead);
            myWindow.document.write(bodyToPrint);
            myWindow.document.write(closingHtml);
            myWindow.document.close();
            myWindow.setTimeout(() => {
                myWindow.focus();
                myWindow.print();
                myWindow.close();
            }, 1000);
        }
    }
}
